import { useEffect, useState, useRef } from "react";
import StickyHeader from "../../components/StickyHeader";
import BestOddsSelector from "../../components/BestOddsSelector";
import CompetitionFilter from "../../components/CompetitionFilter";
import DateSelector from "../../components/DateSelector";
import GamesSelector from "../../components/GamesSelector";
import MarketSelector from "../../components/MarketSelector";
import PillSelector from "../../components/PillSelector";
import Filter from "../../components/Filter";
import PillFilter from "../../components/PillFilter";
import Results from "../../components/Results";
import AlertComponent from "../../components/Alert";
import { RootState } from "../../reducers";
import { useSelector, useDispatch } from "react-redux";
import { getConfig, getFixtures, getSmartPicksByType } from "../../utils/api";
import { ActionType } from "../../types/reducer";
import { LoadingSpinner } from "../../styles/Styles";
import {
  PullToRefreshMessage,
  Page,
  Container,
  SportCard,
  BestOddsButton,
  StickyCompetitionFilter,
  StickyFiltersPanel,
  DisplayContainer,
  ResultPanel,
  PillFiltersPanel,
  FilterSubHeading,
  BestOddsSelectorArrow,
} from "./styles";
import BackArrow from "../../assets/Back_Arrow.png";
import {
  convertNameForOrderBy,
  getKeyByValue,
  findPresetFilterByCompetition,
  showGames,
  getBackGroundColor,
  showDate,
  checkLicensedState,
  getBasedOn,
  showTypeName,
  getCompetitionName,
} from "../../utils";
import { sendAnalyticsEvent } from "../../utils/sendAnalyticMessages";
import dayjs from "dayjs";
import { Event } from "../../types/fixture";
import { marketDataType } from "../../types/marketFilter";
import PullToRefresh from "react-simple-pull-to-refresh";
import { DATES_MAP, SPORTS_MAP } from "../../constants";
import FilterArrow from "../../assets/filter_arrow.svg";

const errorMsg = "Something went wrong, please try again";

interface HomeFilterProps {
  countryCode: string | null;
}

function NewHomeFilter({ countryCode }: HomeFilterProps): JSX.Element {
  const appStore = useSelector((state: RootState) => state.app);
  const filterStore = useSelector((state: RootState) => state.filter);
  const betslipStore = useSelector((state: RootState) => state.betslip);
  const { show, msg } = useSelector((state: RootState) => state.alert);

  const prevCompetitionsRef = useRef(filterStore.competitions);
  const prevDateRef = useRef(filterStore.date);

  const toggleShowBestOdds = appStore && appStore["showBestOdds"];

  const dispatch = useDispatch();
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const parentURL = document.referrer
    ? document.referrer
    : window.location.ancestorOrigins?.[0];

  const setSearchParams = (date: string, market: string, basedOn: string) => {
    dispatch({ type: "SET_DATE_FILTER", payload: date });
    if (date !== "") {
      sendAnalyticsEvent(
        {
          event: "smart_picks_date_select",
          selection: getKeyByValue(DATES_MAP, date),
        },
        parentURL
      );
    }
    dispatch({ type: "SET_MARKET_FILTER", payload: market });
    if (market !== "") {
      sendAnalyticsEvent(
        { event: "smart_picks_market_select", selection: market },
        parentURL
      );
    }
    dispatch({ type: "SET_STATS_FILTER", payload: basedOn });
    if (basedOn !== "") {
      sendAnalyticsEvent(
        { event: "smart_picks_stats_select", selection: basedOn },
        parentURL
      );
    }
  };

  const getFlashpicksConfig = async (countryCode: string | null) => {
    const res = await getConfig(countryCode || "US-NJ");
    const isLicensed = checkLicensedState(countryCode, res.data.usStates);
    dispatch({
      type: "SET_IS_LICENSED",
      payload: isLicensed,
    });

    if (res.data) {
      // manually hiding any markets that aren’t currently shown on the production flashpicks app
      // TODO: will come back and assess this later once have a plan for smart picks pro on web
      const NFL_ALLOWED_MARKETS = [
        "FTS",
        "ATS",
        "PY",
        "REY",
        "RUY",
        "REC",
        "RUA",
        "PAA",
        "PC",
        "SCK",
        "TCK",
        "PTD",
        "INT",
        "P2+TD",
      ];
      const NBA_ALLOWED_MARKETS = [
        "PP",
        "PA",
        "PR",
        "PT",
        "FBS",
        "PDD",
        "PTDD",
        "PS",
        "PTO",
        "PBS",
        "PPA",
        "PPR",
        "PAR",
      ];
      dispatch({
        type: "SET_FLASHPICKS_CONFIG_DATA",
        payload: {
          ...res.data,
          playerMarkets: {
            NBA: res.data.playerMarkets["NBA"].filter((i: marketDataType) =>
              NBA_ALLOWED_MARKETS.includes(i.type)
            ),
            NFL: res.data.playerMarkets["NFL"].filter((i: marketDataType) =>
              NFL_ALLOWED_MARKETS.includes(i.type)
            ),
            MLB: [],
            NHL: [],
          },
        },
      });
    }

    setInitLoading(false);
    return null;
  };

  const getFixturesDate = async (competition: string, date: string) => {
    const res = await getFixtures(competition, date);
    const fixtureIds = res.data.events.map((i: Event) => i.id);

    if (filterStore.games.length !== 0) {
      const updatedGames = filterStore.games.filter((f) =>
        fixtureIds.includes(f)
      );
      dispatch({
        type: "SET_GAMES",
        payload: updatedGames,
      });
    }
    dispatch({
      type: "SET_FIXTURES",
      payload: res.data.events.map((i: Event) => {
        return {
          ...i,
          day: dayjs(i.date).format("dddd DD MMM"),
        };
      }),
    });
  };

  const getSport = () => {
    return filterStore.competitions.map((comp) => {
      return SPORTS_MAP.get(comp);
    });
  };

  const getSmartPicksData = async () => {
    const marketTypes = filterStore.configData.markets.map(
      (i: marketDataType) => i.type
    );
    const result = await getSmartPicksByType(
      marketTypes.includes(filterStore.market) ? "matches" : "players",
      filterStore.market,
      filterStore.statsFilter,
      filterStore.competitions[0],
      filterStore.gamesFilter,
      filterStore.date,
      appStore["activeBookmaker"],
      filterStore.isLicensed && countryCode ? countryCode : "US-NJ",
      filterStore.games.length > 0 ? filterStore.games.toString() : ""
    );

    if (result.data.code === 0) {
      dispatch({ type: "SET_EVENTS", payload: result.data.events });
      sendAnalyticsEvent(
        {
          event: "smart_picks_search",
          competition_selection: filterStore.competitions,
          market_selection: showTypeName(filterStore.market, [
            ...filterStore.configData.markets,
            ...filterStore.configData.playerMarkets[
              getCompetitionName(filterStore)
            ],
          ]),
          stats_selection: showTypeName(
            filterStore.statsFilter,
            getBasedOn(
              filterStore.competitions[0],
              filterStore.configData.basedOn
            )
          ),
          orderby_selection: showTypeName(
            filterStore.gamesFilter,
            filterStore.configData.orderBy
          ),
          sport: getSport(),
        },
        parentURL
      );
    } else {
      dispatch({ type: "OPEN_ALERT", payload: errorMsg });
      dispatch({ type: "SET_EVENTS", payload: [] });
    }
  };

  useEffect(() => {
    if (!initLoading) {
      window.parent.postMessage(
        {
          type: "SET_IFRAME_HEIGHT",
          payload: document.body.scrollHeight,
        },
        parentURL
      );
    }
  }, [initLoading]);

  useEffect(() => {
    getFlashpicksConfig(countryCode);
  }, [countryCode]);

  useEffect(() => {
    const fetchSmartPicksData = async () => {
      setLoading(true);
      const competitionsChanged =
        prevCompetitionsRef.current !== filterStore.competitions;
      const dateChanged = prevDateRef.current !== filterStore.date;

      if (!initLoading && hasCompetition()) {
        const isParamsReady =
          filterStore.date !== "" &&
          filterStore.statsFilter !== "" &&
          filterStore.market !== "";
        if (isParamsReady) {
          if (competitionsChanged || dateChanged) {
            await getFixturesDate(
              filterStore.competitions[0],
              filterStore.date
            );
            await getSmartPicksData();
          } else {
            await getSmartPicksData();
          }
        } else {
          const foundPresetFilter = findPresetFilterByCompetition(
            getCompetitionName(filterStore),
            filterStore.configData.presetFilters,
            [
              ...filterStore.configData.markets,
              ...filterStore.configData.playerMarkets[
                getCompetitionName(filterStore)
              ],
            ]
          );
          const { date, market, basedOn } = foundPresetFilter;
          setSearchParams(date, market, basedOn);
        }
        prevCompetitionsRef.current = filterStore.competitions;
        prevDateRef.current = filterStore.date;
      }
      setLoading(false);
    };
    fetchSmartPicksData();
  }, [
    filterStore.competitions,
    filterStore.date,
    filterStore.games,
    filterStore.market,
    filterStore.statsFilter,
    filterStore.gamesFilter,
    appStore["activeBookmaker"],
  ]);

  const handleRefresh = async () => {
    setLoading(true);
    await getSmartPicksData();
    setLoading(false);
  };

  const hasCompetition = () => {
    return filterStore.competitions.length > 0;
  };

  const renderPillFilters = () => {
    if (
      filterStore.selectedFilter !== "Date" &&
      filterStore.selectedFilter !== "Games" &&
      filterStore.selectedFilter !== "Market"
    ) {
      return (
        <PillFiltersPanel>
          <b>Form:</b>&nbsp;
          <PillFilter
            title="Stats"
            data={showTypeName(
              filterStore.statsFilter,
              getBasedOn(
                filterStore.competitions[0],
                filterStore.configData.basedOn
              )
            )}
          />
          <PillFilter
            title="Order"
            data={convertNameForOrderBy(
              filterStore.gamesFilter,
              showTypeName(
                filterStore.gamesFilter,
                filterStore.configData.orderBy
              )
            )}
          />
        </PillFiltersPanel>
      );
    }
    return null;
  };

  const renderSelectors = () => {
    if (filterStore.selectedFilter !== "") {
      switch (filterStore.selectedFilter) {
        case "Date":
          return <DateSelector />;
        case "Games":
          return <GamesSelector selectedGameIds={filterStore.games} />;
        case "Market":
          return <MarketSelector />;
        case "Stats":
          return (
            <PillSelector
              title="Calculate Stats From"
              data={getBasedOn(
                filterStore.competitions[0],
                filterStore.configData.basedOn
              )}
            />
          );
        case "Order":
          return (
            <PillSelector
              title="Order By"
              data={filterStore.configData.orderBy}
            />
          );
        default:
          return null;
      }
    }
    return null;
  };

  const renderResults = () => {
    const renderPullToRefreshMessage = (
      <PullToRefreshMessage>Release to Refresh</PullToRefreshMessage>
    );
    return (
      <DisplayContainer display={filterStore.selectedFilter === ""}>
        <PullToRefresh
          onRefresh={() => handleRefresh()}
          refreshingContent={renderPullToRefreshMessage}
          pullingContent={renderPullToRefreshMessage}
        >
          <ResultPanel>
            <Results loading={loading} data={filterStore.events} />
          </ResultPanel>
        </PullToRefresh>
      </DisplayContainer>
    );
  };

  const renderMain = () => {
    return (
      <>
        {toggleShowBestOdds && (
          <BestOddsSelector bookmakers={filterStore.configData.bookmakers} />
        )}
        <DisplayContainer display={!toggleShowBestOdds}>
          <StickyCompetitionFilter>
            <CompetitionFilter
              competitions={filterStore.configData.competitions}
            />
          </StickyCompetitionFilter>
          <StickyFiltersPanel>
            <Filter title="Date" data={showDate(filterStore.date)} />
            <Filter
              title="Games"
              data={showGames(filterStore.games, filterStore.fixtures)}
            />
            <Filter
              title="Market"
              data={showTypeName(filterStore.market, [
                ...filterStore.configData.markets,
                ...filterStore.configData.playerMarkets[
                  getCompetitionName(filterStore)
                ],
              ])}
            />
          </StickyFiltersPanel>
          {renderPillFilters()}
          {renderSelectors()}
          {renderResults()}
        </DisplayContainer>
      </>
    );
  };

  const renderBestOdds = () => {
    return (
      <>
        <BestOddsButton
          onClick={() => {
            dispatch({
              type: ActionType.BESTODDS,
              payload: !toggleShowBestOdds,
            });
          }}
        >
          {appStore["activeBookmakerName"]}
        </BestOddsButton>
        <BestOddsSelectorArrow toggleShowBestOdds={toggleShowBestOdds}>
          <img src={FilterArrow} alt="bookmaker selector arrow" />
        </BestOddsSelectorArrow>
      </>
    );
  };

  return (
    <Page isLoading={loading || filterStore.chartLoading}>
      {initLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <StickyHeader
            leftNode={
              hasCompetition() && (
                <img
                  src={BackArrow}
                  alt="back-arrow"
                  loading="lazy"
                  onClick={() => {
                    dispatch({
                      type: "REMOVE_COMPETITION_FROM_SEARCH",
                    });
                    setSearchParams("", "", "");
                    dispatch({
                      type: ActionType.BESTODDS,
                      payload: false,
                    });
                  }}
                />
              )
            }
            rightNode={hasCompetition() && renderBestOdds()}
          />
          <>
            {hasCompetition() ? (
              <div>
                {(loading || filterStore.chartLoading) && <LoadingSpinner />}
                {renderMain()}
              </div>
            ) : (
              <Container>
                <FilterSubHeading>Pick Your Sport</FilterSubHeading>
                <hr />
                {filterStore.configData.competitions.map((i) => (
                  <SportCard
                    key={i}
                    style={{
                      backgroundColor: getBackGroundColor(i),
                    }}
                    onClick={() => {
                      dispatch({
                        type: "ADD_COMPETITION_TO_SEARCH",
                        payload: i,
                      });
                      sendAnalyticsEvent(
                        {
                          event: "smart_picks_competition_select",
                          selection: i,
                        },
                        parentURL
                      );
                    }}
                  >
                    <img
                      src={`https://cdn.checkd-dev.com/flashpicks/competitions/${i}.svg`}
                      alt={`${i} badge`}
                      loading="lazy"
                    />
                  </SportCard>
                ))}
              </Container>
            )}
          </>
          {show && <AlertComponent show={show} msg={msg} />}
        </>
      )}
    </Page>
  );
}

export default NewHomeFilter;
